import {DiagnosedExtractedFieldCard} from './ExtractedFieldCard';
import type {ExtractionDiagnosticoFields} from './ExtractionDiagnosticoSection';

type UnknownRecord = Record<string, unknown>;

export type DocTypeFieldDefinition = {
    label: string;
    aliases: readonly string[];
    formatter?: (value: unknown) => string;
};

function normalizeKey(key: string): string {
    return key.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function asRecord(value: unknown): UnknownRecord | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as UnknownRecord;
}

function getByPath(source: unknown, path: string): unknown {
    const parts = path.split('.');
    let cur: unknown = source;
    for (const part of parts) {
        const rec = asRecord(cur);
        if (!rec) return undefined;
        cur = rec[part];
    }
    return cur;
}

function findValueByNormalizedKey(source: unknown, normalizedTarget: string): unknown {
    const stack: unknown[] = [source];
    while (stack.length > 0) {
        const current = stack.pop();
        const rec = asRecord(current);
        if (!rec) continue;

        for (const [key, value] of Object.entries(rec)) {
            if (normalizeKey(key) === normalizedTarget) {
                return value;
            }

            if (value != null && typeof value === 'object') {
                stack.push(value);
            }
        }
    }
    return undefined;
}

export function normalizeText(value: unknown): string | null {
    if (typeof value === 'string') {
        const text = value.trim();
        return text.length > 0 ? text : null;
    }
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    return null;
}

function unwrapDataField(value: unknown): unknown {
    const rec = asRecord(value);
    if (rec && Object.prototype.hasOwnProperty.call(rec, 'value')) {
        return rec.value;
    }
    return value;
}

export function getFirstValue(extractedData: unknown, aliases: readonly string[]): unknown {
    const root = asRecord(extractedData) ?? {};
    const dados = asRecord(root.dados) ?? {};
    const candidates: unknown[] = [dados, root, root.identity_data, root.metadata];

    for (const alias of aliases) {
        for (const candidate of candidates) {
            const rec = asRecord(candidate);
            if (!rec) continue;

            const direct = rec[alias];
            if (direct !== undefined) return unwrapDataField(direct);

            if (alias.includes('.')) {
                const fromPath = getByPath(rec, alias);
                if (fromPath !== undefined) return unwrapDataField(fromPath);
            }

            const normalized = normalizeKey(alias);
            const recursive = findValueByNormalizedKey(rec, normalized);
            if (recursive !== undefined) return unwrapDataField(recursive);
        }
    }

    return undefined;
}

export function getStringValue(extractedData: unknown, aliases: readonly string[]): string | null {
    return normalizeText(getFirstValue(extractedData, aliases));
}

export function getStringArrayValue(extractedData: unknown, aliases: readonly string[]): string[] {
    const raw = getFirstValue(extractedData, aliases);

    if (Array.isArray(raw)) {
        return raw.map((item) => normalizeText(unwrapDataField(item))).filter((item): item is string => Boolean(item));
    }

    const asText = normalizeText(raw);
    return asText ? [asText] : [];
}

const emptyDisplayValues = new Set(['', '-', '—', 'null', 'undefined']);

export function extractedDisplayValueIsFilled(value: unknown): boolean {
    const raw = unwrapDataField(value);
    if (raw === null || raw === undefined) return false;
    if (typeof raw === 'string') return !emptyDisplayValues.has(raw.trim().toLowerCase());
    if (typeof raw === 'number') return Number.isFinite(raw);
    if (typeof raw === 'boolean') return true;
    if (Array.isArray(raw)) return raw.some(extractedDisplayValueIsFilled);
    if (typeof raw === 'object') {
        return Object.values(raw as Record<string, unknown>).some(extractedDisplayValueIsFilled);
    }
    return false;
}

function fieldDisplayValue(definition: DocTypeFieldDefinition, extractedData: unknown): string {
    const raw = getFirstValue(extractedData, definition.aliases);
    return definition.formatter ? definition.formatter(raw) : normalizeText(raw) ?? '—';
}

export function extractedFieldIsFilled(definition: DocTypeFieldDefinition, extractedData: unknown): boolean {
    const raw = getFirstValue(extractedData, definition.aliases);
    return extractedDisplayValueIsFilled(raw) || extractedDisplayValueIsFilled(fieldDisplayValue(definition, extractedData));
}

export function filledExtractedFieldsFirst<T extends DocTypeFieldDefinition>(
    definitions: readonly T[],
    extractedData: unknown
): T[] {
    return definitions
        .map((definition, index) => ({
            definition,
            filled: extractedFieldIsFilled(definition, extractedData),
            index
        }))
        .sort((left, right) => Number(right.filled) - Number(left.filled) || left.index - right.index)
        .map((item) => item.definition);
}

export function renderExtractedCard(
    definition: DocTypeFieldDefinition,
    extractedData: unknown,
    diagnostico?: ExtractionDiagnosticoFields | null
) {
    const raw = getFirstValue(extractedData, definition.aliases);
    const displayValue = fieldDisplayValue(definition, extractedData);
    const copyValue = normalizeText(raw);

    return (
        <DiagnosedExtractedFieldCard
            key={definition.label}
            label={definition.label}
            displayValue={displayValue}
            copyText={copyValue}
            diagnostico={diagnostico}
            scoreFieldKey={[...definition.aliases]}
        />
    );
}

export function formatCpf(value: unknown): string {
    const text = normalizeText(value);
    if (!text) return '—';
    const digits = text.replace(/\D/g, '');
    if (digits.length !== 11) return text;
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}
